// @flow
/* globals google */
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import GoogleMapContext from './GoogleMapContext';

type Props = {
  children: any,
  lat: any,
  lng: any,
  zIndex?: number
};

const OverlayMarker = ({ lat, lng, children, zIndex = 1 }: Props) => {
  const [overlayAdded, setOverlayAdded] = useState(false);
  const overlayRef = useRef(null);
  const overlayDiv = useRef(null);
  const map = useContext(GoogleMapContext);

  useEffect(() => {
    if (overlayDiv.current) {
      overlayDiv.current.style.zIndex = google.maps.Marker.MAX_ZINDEX + zIndex;
    }
  }, [zIndex, overlayAdded]);

  const onAdd = useCallback(() => {
    const overlayView = overlayRef.current;
    const div = document.createElement('div');

    div.style.borderStyle = 'none';
    div.style.borderWidth = '0px';
    div.style.position = 'absolute';
    div.style.transform = `translate(-50%, -100%)`;
    div.style.zIndex = google.maps.Marker.MAX_ZINDEX + 1;

    const panes = overlayView.getPanes();
    panes.overlayMouseTarget.appendChild(div);

    overlayDiv.current = div;
    setOverlayAdded(true);
  }, [setOverlayAdded, overlayDiv, overlayRef]);

  const draw = useCallback(() => {
    const overlayView = overlayRef.current;

    const overlayProjection = overlayView.getProjection();
    const latLng = new google.maps.LatLng(lat, lng);

    const position = overlayProjection.fromLatLngToDivPixel(latLng);

    const div = overlayDiv.current;

    if (div) {
      div.style.left = `${position.x}px`;
      div.style.top = `${position.y}px`;
    }
  }, [lat, lng, overlayRef, overlayDiv]);

  const onRemove = useCallback(() => {
    const div = overlayDiv.current;

    if (div) {
      div.parentNode.removeChild(div);
      setOverlayAdded(false);
    }
  }, [overlayDiv, setOverlayAdded]);

  useEffect(() => {
    const overlayView = new google.maps.OverlayView();
    overlayRef.current = overlayView;

    overlayView.onAdd = onAdd;
    overlayView.draw = draw;
    overlayView.onRemove = onRemove;

    overlayView.setMap(map);
  }, [map, onAdd, draw, onRemove]);

  useEffect(() => {
    const overlayView = overlayRef.current;

    return () => {
      if (overlayView) {
        overlayView.setMap(null);
      }
    };
  }, [overlayRef]);

  return overlayAdded ? createPortal(children, overlayDiv.current) : null;
};

export default OverlayMarker;
